import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { decodeBase64, STATUSES } from "../comman/status";
import { MOMENT_KEY, STORAGE_KEY, STORAGE_KEY_REFRESH,AUTH_LOGIN } from "../utils/config";
import AuthService from "../services/authService";
import { LoginInfo } from "../models/login";
import { identify } from "../analytics/trackingPlan";
import { trackLoginSucceeded, trackSwitchUser, trackTotpDoLater } from "../analytics/authAnalytics";
import { rudderReset } from "../analytics/rudder";

/**
 * The trading account being operated. Null before sign-in, until an account is
 * chosen, and after sign-out. `clientCode` is unset while an account is still
 * being opened; `accountType` is the backend's own name for the kind
 * ("Individual", "HUF", …), which analytics maps (analytics/trackingPlan.ts).
 */
export interface ActiveAccount {
    clientCode?: string;
    accountType?: string;
}

// Define the shape of your state
interface AuthState {
    user: LoginInfo | null; // Define a more specific type if possible
    isNewUser: boolean;
    isLoggedIn: boolean;
    status: string;
    error_message: string | null;
    email: string | null;
    mobile: string | null;
    data: any | null; // Define a more specific type if possible
    user_id: string | null;
    themeData: any; // Define a more specific type if possible
    theamDataBG:any;
    tokenExp: {
        ssoToken: boolean;
        checked?: boolean;
    };
    sessionTime: number;
    isOTPVerified:boolean; // for TOPT and OPT its true
    activeAccount: ActiveAccount | null;
}

// Initial state - Fixed to handle undefined localStorage values
const getUserFromStorage = () => {
    try {
        const userData = localStorage.getItem(STORAGE_KEY);
        if (userData && userData !== 'undefined' && userData !== 'null') {
            return JSON.parse(userData);
        }
        return null;
    } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        localStorage.removeItem(STORAGE_KEY);
        return null;
    }
};

const getSessionTimeFromStorage = () => {
    try {
        const sessionData = localStorage.getItem(MOMENT_KEY);
        if (sessionData && sessionData !== 'undefined' && sessionData !== 'null') {
            return JSON.parse(sessionData);
        }
        return 0;
    } catch (error) {
        console.error('Error parsing session time from localStorage:', error);
        localStorage.removeItem(MOMENT_KEY);
        return 0;
    }
};

const getRefreshDataFromStorage = () => {
    try {
        const refreshData = localStorage.getItem(STORAGE_KEY_REFRESH);
        if (refreshData && refreshData !== 'undefined' && refreshData !== 'null') {
            return JSON.parse(refreshData);
        }
        return null;
    } catch (error) {
        console.error('Error parsing refresh data from localStorage:', error);
        localStorage.removeItem(STORAGE_KEY_REFRESH);
        return null;
    }
};

const user = getUserFromStorage();
const sessionTime = getSessionTimeFromStorage();
const refreshData = getRefreshDataFromStorage();

const initialState: AuthState = {
    user,
    isNewUser: false,
    isLoggedIn: !!user,
    status: STATUSES.IDLE,
    error_message: null,
    email: null,
    mobile: null,
    data: refreshData,
    user_id: null,
    themeData: null,
    theamDataBG: null,
    tokenExp: { ssoToken: false },
    sessionTime: Number(sessionTime),
    isOTPVerified: false,
    activeAccount: null
};

function checkTokenValid(state: AuthState) {
    const sso_token = state.user?.ssoToken;
    if (sso_token) {
        let decodeValue = JSON.parse(decodeBase64(sso_token.split('.')[1]));
        state.data = { ...state.data, applicationId: decodeValue.applicationId };
        let d = new Date(0);
        let momentInTime = moment(d.setUTCSeconds(decodeValue.exp));
        let now = moment();
        let val = now.isAfter(momentInTime);
        let diff = now.diff(momentInTime, 'minutes');
        if (diff > 0) {
            state.tokenExp = { ...state.tokenExp, ssoToken: true };
        } else {
            state.tokenExp = { ...state.tokenExp, ssoToken: false, checked: true };
        }
    }
}

const authReducer = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setUser(state, action: PayloadAction<LoginInfo>) {
            if(action.payload.identifier){
                // Identity is keyed ONLY on identifier — never the mobile number.
                // No account on the traits: they belong to the person, and the
                // account changes on every switch.
                identify(action.payload.identifier, {
                    email: action.payload.email,
                    mobile: action.payload.mobile,
                    name: action.payload.user_name,
                  })
            }
            state.user = action.payload;
            state.isLoggedIn = true;
            // A sign-in starts with no account chosen, never the last person's.
            state.activeAccount = null;
        },
        setStatus(state, action: PayloadAction<string>) {
            state.status = action.payload;
        },
        setMessage(state, action: PayloadAction<string>) {
            state.error_message = action.payload;
        },
        clearMessage(state) {
            state.error_message = null;
        },
        logout(state) {
            // Clear the RudderStack identity so the next user on this browser
            // doesn't inherit the previous user's id / anonymous mapping.
            rudderReset();
            state.isLoggedIn = false;
            state.user = null;
            state.activeAccount = null;
        },
        setActiveAccount(state, action: PayloadAction<ActiveAccount>) {
            state.activeAccount = action.payload;
        },
        setMpinEnable(state) {
            if (state.user) {
                state.user.mpin_enabled = true;
            }
        },
        setEmailAndMobile(state, action: PayloadAction<{ email: string | null; mobile: string | null}>) {
            state.email = action.payload.email;
            state.mobile = action.payload.mobile;
        },
        setQrData(state, action: PayloadAction<{ data: any }>) {
            state.data = action.payload.data;
        },
        setOtpData(state, action: PayloadAction<{ data: any; user_id: string | null }>) {
            state.data = action.payload.data;
            state.user_id = action.payload.user_id;
        },
        updateAuthTokenForSession(state, action: PayloadAction<{ data: any }>) {
            state.data.token = action.payload.data;
        },
        setSessionTime(state, action: PayloadAction<number>) {
            state.sessionTime = action.payload;
        },
        setThemeData(state, action: PayloadAction<any>) {
            state.themeData = action.payload;
        },
        initialCheckData(state) {
            checkTokenValid(state);
        },
        loginEvent(state, action: PayloadAction<{ mobile?: string | null; identifier?: string | null; email?: string | null } | undefined>) {
            trackLoginSucceeded({
                mobile: action.payload?.mobile,
                identifier: action.payload?.identifier,
            });
        },
        switchUserEvent(state) {
            trackSwitchUser();
        },
        TotpDoLaterEvent(state) {
            trackTotpDoLater();
        },
        // we will set isOldUser to true if the user is old user
        setNewUser(state,action:PayloadAction<boolean>){
            state.isNewUser = action.payload
        },
        // set isOTPVerified to true
        setOTPVerified(state){
            state.isOTPVerified = true
        }
    },
});

export const currentUser = (state: { auth: AuthState }) => state.auth.user;
export const { setUser, setStatus, setMessage, clearMessage, setThemeData,
    setEmailAndMobile, setOtpData, setSessionTime, logout, loginEvent, setMpinEnable, initialCheckData,
    switchUserEvent, TotpDoLaterEvent, setQrData,updateAuthTokenForSession,setNewUser,setOTPVerified, setActiveAccount } = authReducer.actions;
export default authReducer.reducer;